import { useMemo } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClosedDate,
  CreateClosedDate,
  CreateTimeOff,
  HourException,
  SetHourException,
  Staff,
  TimeOff,
  TimeOffConflictResult,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS7 phase 1 scheduling-depth hooks: staff time-off, salon closed-dates, and per-date working-hour
 * exceptions. These are SALON-wide calendar facts (not renter-sealed) the diary and rota editor read
 * so the diary reflects who is actually in and when the salon is actually open. Mirrors the patterns
 * in data/services.ts / data/staff.ts: TanStack Query + the generated client via unwrap()/expectOk().
 *
 * RBAC (mirror of the API): time-off and hour-exceptions are manager-or-self; closed-dates are
 * manager-only. The UI gates accordingly — a server 403 is never the first feedback a user gets.
 */

// ── Closed dates (salon-wide) ──────────────────────────────────────────────────

export function useClosedDates() {
  return useQuery({
    queryKey: qk.closedDates,
    queryFn: async (): Promise<ClosedDate[]> => unwrap(await api.GET('/v1/closed-dates')),
  });
}

export function useCreateClosedDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateClosedDate): Promise<ClosedDate> =>
      unwrap(await api.POST('/v1/closed-dates', { body: body as never })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.closedDates }),
  });
}

export function useDeleteClosedDate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/closed-dates/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.closedDates }),
  });
}

// ── Time off (per staff) ───────────────────────────────────────────────────────

export function useTimeOff(staffId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.timeOff(staffId ?? ''),
    enabled: Boolean(staffId) && enabled,
    queryFn: async (): Promise<TimeOff[]> =>
      unwrap(await api.GET('/v1/staff/{id}/time-off', { params: { path: { id: staffId! } } })),
  });
}

/**
 * Create time off for a staff member. The response carries the new entry PLUS any of that member's
 * existing appointments that overlap the off period — a WARNING the caller surfaces (never an
 * auto-cancel). Returns the full conflict result so the form can show the rebook/notify list.
 */
export function useCreateTimeOff(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTimeOff): Promise<TimeOffConflictResult> =>
      unwrap(
        await api.POST('/v1/staff/{id}/time-off', {
          params: { path: { id: staffId } },
          body: body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.timeOff(staffId) }),
  });
}

export function useDeleteTimeOff(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/time-off/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.timeOff(staffId) }),
  });
}

// ── Working-hour exceptions (per staff, keyed on date) ─────────────────────────

export function useHourExceptions(staffId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: qk.hourExceptions(staffId ?? ''),
    enabled: Boolean(staffId) && enabled,
    queryFn: async (): Promise<HourException[]> =>
      unwrap(
        await api.GET('/v1/staff/{id}/hour-exceptions', { params: { path: { id: staffId! } } }),
      ),
  });
}

/** Upsert a date-specific working-hour exception (closed / custom hours / break), keyed on date. */
export function useSetHourException(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: SetHourException): Promise<HourException> =>
      unwrap(
        await api.PUT('/v1/staff/{id}/hour-exceptions', {
          params: { path: { id: staffId } },
          body: body as never,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.hourExceptions(staffId) }),
  });
}

export function useDeleteHourException(staffId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/hour-exceptions/{id}', { params: { path: { id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.hourExceptions(staffId) }),
  });
}

// ── Diary aggregator ───────────────────────────────────────────────────────────

/** Per-staff scheduling facts for the diary, keyed by staffId. Empty arrays while loading. */
export interface DayCalendar {
  closedDate: ClosedDate | null;
  timeOffByStaff: Map<string, TimeOff[]>;
  exceptionsByStaff: Map<string, HourException[]>;
}

/**
 * Fetch everything the diary needs to render calendar realism for `date`: the salon's closed-dates
 * (filtered to the viewed date) plus each chair's time-off and hour-exceptions. Uses useQueries so a
 * variable chair count stays inside the rules of hooks; lists are cached per-staff under the same
 * keys the rota editor invalidates, so a change there refreshes the diary for free.
 */
export function useDayCalendar(date: string, chairs: Staff[]): DayCalendar {
  const closed = useClosedDates();
  const ids = chairs.map((c) => c.id);

  const timeOffResults = useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.timeOff(id),
      queryFn: async (): Promise<TimeOff[]> =>
        unwrap(await api.GET('/v1/staff/{id}/time-off', { params: { path: { id } } })),
    })),
  });
  const exceptionResults = useQueries({
    queries: ids.map((id) => ({
      queryKey: qk.hourExceptions(id),
      queryFn: async (): Promise<HourException[]> =>
        unwrap(await api.GET('/v1/staff/{id}/hour-exceptions', { params: { path: { id } } })),
    })),
  });

  // Index by mapped-over fingerprints so the memo only recomputes when the underlying data changes.
  const closedDate = (closed.data ?? []).find((c) => c.date === date) ?? null;
  const timeOffData = timeOffResults.map((r) => r.data);
  const exceptionData = exceptionResults.map((r) => r.data);

  return useMemo<DayCalendar>(() => {
    const timeOffByStaff = new Map<string, TimeOff[]>();
    const exceptionsByStaff = new Map<string, HourException[]>();
    ids.forEach((id, i) => {
      timeOffByStaff.set(id, timeOffData[i] ?? []);
      exceptionsByStaff.set(id, exceptionData[i] ?? []);
    });
    return { closedDate, timeOffByStaff, exceptionsByStaff };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, ids.join(','), closedDate, ...timeOffData, ...exceptionData]);
}
